import { lazy, Suspense, useEffect } from 'react';
import { Footer } from './components/Footer.tsx';
import { Header } from './components/Header.tsx';
import { ToastHost } from './lib/toast.tsx';
import { api } from './lib/api.ts';
import { useFavorites } from './lib/favorites.ts';
import { navigate, useRoute } from './lib/router.ts';
import { LandingPage } from './pages/LandingPage.tsx';
import { DetailPage } from './pages/DetailPage.tsx';
import { FavoritesPage } from './pages/FavoritesPage.tsx';
import { CategoryPage } from './pages/CategoryPage.tsx';
import { MePage } from './pages/MePage.tsx';
import { LegalPage } from './pages/LegalPage.tsx';
import { CollectionPage } from './pages/CollectionPage.tsx';
import { BrowseCollectionsPage } from './pages/BrowseCollectionsPage.tsx';
import { RequestsPage } from './pages/RequestsPage.tsx';
import { ContactPage } from './pages/ContactPage.tsx';

// admin UI = 빌드 상수 게이트. prod 빌드에선 ADMIN_UI=false → AdminApp=null → 런타임에
// 절대 렌더 안 됨 + router.ts 가 prod 에서 /admin* 라우팅 차단 → 이 dynamic import 는
// 실행되지 않음.
// 주의: Bun 은 이 dead 분기의 import() 를 DCE 하지 '않고' admin chunk 를 그대로 emit 한다.
// 실제 prod 제외는 build.ts 의 명시적 prune(ADMIN_CHUNK_MARKER 매칭 + fail-closed)이 담당.
// 즉 prod 번들의 dangling import("/chunk-admin.js") 는 파일이 삭제돼 있어도 호출 안 되므로 무해.
const ADMIN_UI = process.env.NODE_ENV !== 'production';
const AdminApp = ADMIN_UI ? lazy(() => import('./AdminApp.tsx')) : null;

export function App({ initialPath }: { initialPath?: string } = {}) {
  const route = useRoute(initialPath);
  const [favs, toggleFav] = useFavorites();
  const onOpen = (id: string) => navigate('/s/' + id);

  // 라우트 바뀔 때마다 pageview ping (DAU 집계). 클라 전용 (useEffect).
  useEffect(() => {
    api.pageview(window.location.pathname).catch(() => {});
  }, [route]);

  const isAdmin = route.page.startsWith('admin');

  let content;
  if (isAdmin && AdminApp) {
    content = (
      <Suspense
        fallback={<main className="nf-page" style={{ padding: '120px 56px', color: 'var(--fg-muted)' }}>로딩…</main>}
      >
        <AdminApp route={route} />
      </Suspense>
    );
  } else {
    // 비admin (prod 에선 admin 경로도 router 가 landing 으로 떨궈 여기로 옴).
    let page;
    if (route.page === 'detail') {
      page = <DetailPage serviceId={route.id} favs={favs} onFav={toggleFav} onOpen={onOpen} />;
    } else if (route.page === 'favorites') {
      page = <FavoritesPage tab={route.tab} favs={favs} onFav={toggleFav} onOpen={onOpen} />;
    } else if (route.page === 'category') {
      page = <CategoryPage slug={route.slug} favs={favs} onFav={toggleFav} onOpen={onOpen} />;
    } else if (route.page === 'me') {
      page = <MePage favs={favs} />;
    } else if (route.page === 'collection') {
      page = <CollectionPage code={route.code} favs={favs} onFav={toggleFav} onOpen={onOpen} />;
    } else if (route.page === 'collections') {
      page = <BrowseCollectionsPage />;
    } else if (route.page === 'requests') {
      page = <RequestsPage />;
    } else if (route.page === 'contact') {
      page = <ContactPage />;
    } else if (route.page === 'legal') {
      page = <LegalPage kind={route.kind} />;
    } else {
      page = <LandingPage favs={favs} onFav={toggleFav} onOpen={onOpen} />;
    }
    content = (
      <>
        <Header route={route} favs={favs} />
        {page}
        <Footer />
      </>
    );
  }

  return (
    <>
      {content}
      <ToastHost />
    </>
  );
}

export default App;
